import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router-dom";

import { format } from "date-fns";
import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFOptionList,
  PDFRadioGroup,
  PDFTextField
} from "pdf-lib";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import type { FolderObject } from "components/folder-picker/folder-picker.component";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import {
  useGetBoxAccessTokenQuery,
  useUploadDocumentMutation
} from "services/e-documents";
import { useLazyGetFormFileUrlQuery } from "services/forms";
import { useGetPatientQuery } from "services/patients";
import {
  PRINT_TAB_BLOCKED_MESSAGE,
  blobToUint8Array,
  claimPrintTab,
  convertBlobUrlToFile
} from "utils/common";

import PreviewPdfFormDialog from "./preview-pdf-form-dialog.component";

type GeneratePdfFn = (
  data?: Record<string, any>
) => Promise<Blob | undefined | null>;

type AppointmentPrintFormProviderProps = {
  patientId?: number;
  formName: string;
  showSignButton?: boolean;
  children?: React.ReactNode;
};

type PreviewPdfFormContextType = {
  pdfBlobUrl: string | undefined;
  folderPickerOpen: boolean;
  isEdocsAvailable: boolean;
  boxFolderId?: string;
  boxAccessToken?: string | null;
  handleShowSignModal: () => void;
  handleHideSignModal: () => void;
  handleShowPreviewModal: (title?: string) => void;
  handleClosePreviewModal: (object?: any, reason?: string) => void;
  handlePrint: () => void;
  handleSaveToEdocs: () => void;
  handleSubmitSignature: (files: File[], uploadToEdocs?: boolean) => void;
  handleOpenBoxDialog: () => void;
  handleHideBoxDialog: () => void;
  handleSelectBoxFolder: (
    selectedFolder: FolderObject | null,
    documentName: string
  ) => Promise<void>;
  /** The name the PDF is filed under in E-Docs unless staff type another. */
  fileName: string;
  loadPdfContent: (params: {
    formId?: number;
    blob?: Blob;
    signature?: File | null;
    generatePdfFn?: GeneratePdfFn;
  }) => Promise<void>;
  open: boolean;
  isReady: boolean;
  showSignModal: boolean;
  title: string;
  signature?: File | null;
  numPages: number | null;
  setNumPages: React.Dispatch<React.SetStateAction<number | null>>;
  pdfBlobRef: React.MutableRefObject<Blob | undefined>;
  userData: UserDataType;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  showSignButton?: boolean;
};

type UserDataType = {
  byFieldName: Record<string, any>;
  byFieldId: Record<string, any>;
};

async function applyFieldsToPdfDoc(
  pdfDoc: PDFDocument,
  byFieldId: Record<string, any>
): Promise<Uint8Array> {
  const form = pdfDoc.getForm();
  form.getFields().forEach((field) => {
    const value = byFieldId[field.getName()];
    if (value === undefined) return;
    if (field instanceof PDFTextField) {
      field.setText(String(value));
    } else if (field instanceof PDFCheckBox) {
      if (field.isChecked() !== value) {
        value ? field.check() : field.uncheck();
        field.defaultUpdateAppearances();
      }
    } else if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
      field.select(String(value));
    } else if (field instanceof PDFRadioGroup) {
      field.select(String(value));
    }
  });
  return pdfDoc.save({ updateFieldAppearances: true });
}

const PreviewPdfFormContext = createContext<PreviewPdfFormContextType | null>(
  null
);

export function PreviewPdfFormProvider({
  patientId: providerPatientId,
  formName,
  showSignButton,
  children
}: AppointmentPrintFormProviderProps) {
  const dispatch = useAppDispatch();
  const params = useParams();

  const patientId = useMemo(() => {
    if (providerPatientId && providerPatientId !== -1) {
      return providerPatientId;
    }
    return (
      Number(
        (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
      ) || undefined
    );
  }, [providerPatientId, params]);

  const [formId, setFormId] = useState<number | undefined>();
  const [isBusy, setIsBusy] = useState(false);

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);

  const [getFormFileUrl, { isLoading: isGetFormFileUrlLoading }] =
    useLazyGetFormFileUrlQuery();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  const [showSignModal, setShowSignModal] = useState(false);

  const generatePdfFnRef = useRef<GeneratePdfFn>();
  const [signature, setSignature] = useState<File | undefined | null>();
  // signatureRef mirrors `signature` state for synchronous reads inside
  // callbacks (avoids stale closures from React's async state batching).
  const signatureRef = useRef<File | undefined | null>();

  const pdfBlobRef = useRef<Blob | undefined>();
  const isGeneratingPdfRef = useRef(false);

  const updateSignature = useCallback((sig: File | undefined | null) => {
    signatureRef.current = sig;
    setSignature(sig);
  }, []);

  const pdfDocumentRef = useRef<PDFDocument | undefined>();
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | undefined>();
  const [userData, setUserData] = useState<UserDataType>({
    byFieldName: {},
    byFieldId: {}
  });

  useEffect(() => {
    return () => {
      if (pdfBlobUrl && pdfBlobUrl.startsWith("blob")) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const [folderPickerOpen, setFolderPickerOpen] = useState(false);

  const { data: boxData } = useGetBoxAccessTokenQuery();

  const { data: patient, isFetching: isPatientFetching } = useGetPatientQuery(
    { patientId: Number(patientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !Boolean(patientId) || patientId === -1
    }
  );

  // Latch the Box access token to its first truthy value while the folder
  // picker is open, then reset it on close. The picker refreshes its own
  // token internally right before calling Box's show(), and that refresh
  // updates the shared getBoxAccessToken cache. Reading the live cache value
  // here would feed that churn back into the picker's mount gate and token
  // prop, producing a token-refetch/remount loop, so we hold a stable token
  // for as long as the dialog stays open.
  const [stableBoxToken, setStableBoxToken] = useState<string | null>(null);

  useEffect(() => {
    if (!folderPickerOpen) {
      setStableBoxToken(null);
      return;
    }
    setStableBoxToken((prev) => prev ?? boxData?.box_access_token ?? null);
  }, [folderPickerOpen, boxData?.box_access_token]);

  const isEdocsAvailable = Boolean(patient?.box_folder_id && stableBoxToken);

  const fileName = useMemo(() => {
    return patient
      ? `${formName}-${patient?.first_name} ${patient?.last_name} - ${format(
          new Date(),
          "yyyyMMddHHmmss"
        )}.pdf`
      : `${formName}.pdf`;
  }, [formName, patient]);

  const [uploadDocument, { isLoading: isUploadingToEDocs }] =
    useUploadDocumentMutation();

  const initPdf = useCallback(
    async (blob: Blob | null | undefined, url?: string) => {
      if (blob) {
        pdfBlobRef.current = blob;

        if (pdfBlobUrl && pdfBlobUrl.startsWith("blob")) {
          URL.revokeObjectURL(pdfBlobUrl);
        }

        const pdfDoc = await PDFDocument.load(await blobToUint8Array(blob));
        setNumPages(pdfDoc.getPageCount());

        if (!url) {
          setPdfBlobUrl(URL.createObjectURL(blob));
        } else {
          setPdfBlobUrl(url);
        }

        pdfDocumentRef.current = pdfDoc;

        return {
          blob,
          url,
          pdfDoc
        };
      }

      return Promise.resolve({});
    },
    [pdfBlobUrl]
  );

  const getPdfFormInputs = useCallback(
    () =>
      pdfContainerRef.current?.querySelectorAll("input, textarea, select") ||
      [],
    []
  );

  const collectUserData = useCallback(() => {
    const data: UserDataType = {
      byFieldId: {},
      byFieldName: {}
    };

    if (!pdfDocumentRef.current) {
      setUserData(data);
      return data;
    }

    const inputs = getPdfFormInputs();

    inputs?.forEach((input) => {
      const fieldId = input.getAttribute("name");
      const fieldName = input.getAttribute("data-element-id") ?? "";
      if (!fieldId || !fieldName) return;

      let value: string | boolean | undefined = undefined;

      if (input instanceof HTMLInputElement && input.type === "checkbox") {
        value = input.checked;
      } else {
        value = (input as HTMLInputElement).value;
      }

      data.byFieldId[fieldId] = value;
      data.byFieldName[fieldName] = value;
    });

    setUserData(data);

    return data;
  }, [getPdfFormInputs]);

  const applyUserDataToPdf = useCallback(
    async (initialData?: UserDataType) => {
      if (!pdfDocumentRef.current) {
        return Promise.resolve({
          byFieldId: {},
          byFieldName: {}
        });
      }

      const data = initialData ? initialData : collectUserData();

      const filledPdfBytes = await applyFieldsToPdfDoc(
        pdfDocumentRef.current,
        data.byFieldId
      );

      await initPdf(new Blob([filledPdfBytes], { type: "application/pdf" }));

      return data;
    },
    [collectUserData, initPdf]
  );

  const generatePdf = useCallback(
    async (signature: File | undefined | null) => {
      const fn = generatePdfFnRef.current;
      if (fn && pdfDocumentRef.current) {
        if (isGeneratingPdfRef.current) return false;
        isGeneratingPdfRef.current = true;
        setIsBusy(true);
        try {
          // Snapshot field values from DOM before the backend call replaces pdfDocumentRef
          const data = collectUserData();
          // Call backend — returns PDF with signature but without user's field edits
          const blob = await fn({ ...data.byFieldName, signature });
          if (!blob) {
            return false;
          }
          // If the user edited any fields, re-apply them to the backend PDF before displaying/uploading
          if (Object.keys(data.byFieldId).length > 0) {
            const pdfDoc = await PDFDocument.load(await blobToUint8Array(blob));
            const filledBytes = await applyFieldsToPdfDoc(
              pdfDoc,
              data.byFieldId
            );
            await initPdf(new Blob([filledBytes], { type: "application/pdf" }));
          } else {
            await initPdf(blob, undefined);
          }
          return true;
        } catch (error) {
          console.error("PDF generation failed:", error);
          dispatch(
            setCustomMessage("Failed to generate PDF. Please try again.")
          );
          dispatch(setSnackbarOpen(true));
          return false;
        } finally {
          isGeneratingPdfRef.current = false;
          setIsBusy(false);
        }
      }
      return false;
    },
    [collectUserData, dispatch, initPdf]
  );

  const handleShowSignModal = useCallback(async () => {
    if (isGeneratingPdfRef.current) return;
    await applyUserDataToPdf();
    setShowSignModal(true);
  }, [applyUserDataToPdf]);

  const handleHideSignModal = useCallback(() => {
    setShowSignModal(false);
  }, []);

  const handleShowPreviewModal = useCallback((title?: string) => {
    setOpen(true);
    setTitle(title || "");
  }, []);

  const handleClosePreviewModal = useCallback(
    (object?: any, reason?: string) => {
      if (reason === "backdropClick") {
        return;
      }
      setOpen(false);
    },
    []
  );

  const handlePrint = useCallback(async () => {
    if (!pdfBlobRef.current || isGeneratingPdfRef.current) return;

    // Claimed before the await: opening the tab afterwards outlives the click's user
    // activation and the browser blocks it. See claimPrintTab.
    const printTab = claimPrintTab("the form");
    if (!printTab) {
      dispatch(setCustomMessage(PRINT_TAB_BLOCKED_MESSAGE));
      dispatch(setSnackbarOpen(true));
      return;
    }

    try {
      await applyUserDataToPdf();
    } catch (error) {
      printTab.close();
      throw error;
    }

    const fileUrl = window.URL.createObjectURL(pdfBlobRef.current);
    // Deliberately never revoked: the print tab reads this URL asynchronously and
    // revoking it made the tab fail with "cannot open file" on slower devices (SN-19205).
    void printTab.print(fileUrl);
  }, [applyUserDataToPdf, dispatch]);

  const loadPdfContent = useCallback(
    async (params: {
      formId?: number;
      blob?: Blob;
      signature?: File | null;
      generatePdfFn?: GeneratePdfFn;
    }) => {
      const { blob, formId, signature, generatePdfFn } = params;
      setIsBusy(false);

      if (formId) {
        const response = await getFormFileUrl({ formId: Number(formId) });

        if (response.data) {
          setFormId(formId);
          const url = response.data;
          setPdfBlobUrl(url);

          convertBlobUrlToFile(url, fileName).then((file) => {
            initPdf(file, undefined);
          });
        }
      } else if (blob) {
        initPdf(blob, undefined);
      }

      if (signature !== undefined) {
        updateSignature(signature);
      }

      if (generatePdfFn !== undefined) {
        generatePdfFnRef.current = generatePdfFn;
      }
    },
    [fileName, getFormFileUrl, initPdf, updateSignature]
  );

  const handleOpenBoxDialog = useCallback(() => {
    setFolderPickerOpen(true);
  }, []);

  const handleSaveToEdocs = useCallback(async () => {
    const success = await generatePdf(signatureRef.current);
    if (success) {
      handleOpenBoxDialog();
    }
  }, [generatePdf, handleOpenBoxDialog]);

  const handleSubmitSignature = useCallback(
    async (files: File[], uploadToEdocs?: boolean) => {
      const signature = files?.at(0);
      updateSignature(signature);

      handleHideSignModal();

      const success = await generatePdf(signature);

      if (uploadToEdocs && success) {
        setFolderPickerOpen(true);
      }
    },
    [generatePdf, handleHideSignModal, updateSignature]
  );

  const handleHideBoxDialog = useCallback(() => {
    setFolderPickerOpen(false);
  }, []);

  const handleSelectBoxFolder = useCallback(
    async (selectedFolder: FolderObject | null, documentName: string) => {
      const formData = new FormData();
      const blob = pdfBlobRef.current;

      if (patient && blob) {
        formData.append("document", blob, documentName);
        formData.append(
          "destination_folder_id",
          String(selectedFolder?.id || patient.box_folder_id)
        );

        uploadDocument({
          id: patient.id,
          type: "patients",
          formData: formData
        }).then((response) => {
          if (response && "error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setSnackbarOpen(true));
          } else {
            dispatch(
              setCustomMessage("PDF File Was Uploaded to Selected Folder")
            );
            dispatch(setSnackbarOpen(true));
          }
        });
      }
    },
    [dispatch, patient, uploadDocument]
  );

  const isReady = useMemo(() => {
    return (
      Boolean(patient) &&
      !isPatientFetching &&
      !isUploadingToEDocs &&
      !isGetFormFileUrlLoading &&
      !isBusy
    );
  }, [
    patient,
    isPatientFetching,
    isUploadingToEDocs,
    isGetFormFileUrlLoading,
    isBusy
  ]);

  const value = useMemo(() => {
    return {
      folderPickerOpen,
      isEdocsAvailable,
      boxFolderId: patient?.box_folder_id,
      boxAccessToken: stableBoxToken,
      pdfBlobUrl,
      handleShowSignModal,
      handleHideSignModal,
      handleClosePreviewModal,
      handlePrint,
      handleSaveToEdocs,
      handleSubmitSignature,
      handleOpenBoxDialog,
      handleHideBoxDialog,
      handleSelectBoxFolder,
      fileName,
      open,
      isReady,
      signature,
      showSignModal,
      loadPdfContent,
      formId,
      handleShowPreviewModal,
      title,
      numPages,
      setNumPages,

      pdfBlobRef,

      userData,
      pdfContainerRef,
      showSignButton
    };
  }, [
    folderPickerOpen,
    isEdocsAvailable,
    patient?.box_folder_id,
    stableBoxToken,
    pdfBlobUrl,
    handleShowSignModal,
    handleHideSignModal,
    handleClosePreviewModal,
    handlePrint,
    handleSaveToEdocs,
    handleSubmitSignature,
    handleOpenBoxDialog,
    handleHideBoxDialog,
    handleSelectBoxFolder,
    fileName,
    open,
    isReady,
    signature,
    showSignModal,
    loadPdfContent,
    formId,
    handleShowPreviewModal,
    title,
    numPages,
    userData,
    showSignButton
  ]);

  return (
    <PreviewPdfFormContext.Provider value={value}>
      {children}
      <PreviewPdfFormDialog />
    </PreviewPdfFormContext.Provider>
  );
}

export default function usePreviewPdfForm() {
  const context = useContext(PreviewPdfFormContext);
  if (!context) {
    throw new Error(
      `usePreviewPdfForm must be used within an PreviewPdfFormProvider`
    );
  }

  return context;
}
